import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AuditHistoryApi,
  type AuditEntry,
  type AuditObjectType,
  type AuditType,
} from '@/services/shared/auditHistoryApi'
import { AuthService } from '@/services/core/auth'

/** The changes that landed in one bucket of the chart's time axis. */
export interface ChangeMarker {
  /** The x value on the chart — the bucket's own label. */
  label: string
  /** Start of the bucket, for ordering and for the tooltip. */
  chunkStart: string
  entries: AuditEntry[]
}

/**
 * Buckets changes onto the chart's time axis: a change belongs to the chunk it
 * happened inside, so the marker lines up with the delivery it may have moved.
 * Changes outside the charted period are dropped.
 */
export function groupChangesByChunk(
  entries: AuditEntry[],
  /** ISO start of each chunk, as `chunkRange` reports them. */
  chunkStarts: string[],
  /** The chart's label for each chunk, index-aligned with `chunkStarts`. */
  labels: string[]
): ChangeMarker[] {
  if (chunkStarts.length === 0) return []
  const starts = chunkStarts.map((iso) => new Date(iso).getTime())
  // The last chunk runs to the next boundary, which the range does not name;
  // assume it is as wide as the one before it.
  const width = starts.length > 1 ? starts[starts.length - 1] - starts[starts.length - 2] : 0
  const end = starts[starts.length - 1] + width

  const byIndex = new Map<number, AuditEntry[]>()
  for (const entry of entries) {
    const time = new Date(entry.timestamp).getTime()
    if (Number.isNaN(time) || time < starts[0] || time >= end) continue
    // The chunk it falls in is the last one that started at or before it.
    let index = starts.length - 1
    while (index > 0 && starts[index] > time) index--
    const existing = byIndex.get(index)
    if (existing) existing.push(entry)
    else byIndex.set(index, [entry])
  }

  return [...byIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, grouped]) => ({
      label: labels[index],
      chunkStart: chunkStarts[index],
      // Oldest first inside a bucket — the audit API answers newest first.
      entries: grouped.slice().reverse(),
    }))
}

export interface ChangeMarkerQuery {
  objectType: AuditObjectType
  objectId: string
  /** The charted period, so only changes that could show up are fetched. */
  startDate: string
  endDate: string
  /**
   * Which changes to mark. The default is the API's own classification for
   * changes that can move delivery — a renamed line item does not belong on a
   * delivery chart.
   */
  auditType?: AuditType
  enabled?: boolean
}

/** The object's changes within the charted period, newest first. */
export function useChangeMarkers({
  objectType,
  objectId,
  startDate,
  endDate,
  auditType = 'DIRECT_DELIVERY_IMPACT',
  enabled = true,
}: ChangeMarkerQuery) {
  const result = useQuery({
    queryKey: ['audithistory-markers', objectType, objectId, startDate, endDate, auditType],
    queryFn: () =>
      AuditHistoryApi.getPage(objectType, objectId, {
        page: 1,
        pageSize: 100,
        auditType,
        startDate,
        endDate,
      }),
    enabled: enabled && AuthService.isAuthenticated() && !!objectId,
  })

  return useMemo(() => result.data?.results ?? [], [result.data])
}
